import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, switchMap } from 'rxjs';
import { WIDGET_DASHBOARD_ID_HEADER } from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../../../app.config';
import { IDropdownOption } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../state/state.interface';
import { IGetManyResponse } from '../../../../../utilities/http-utilities.service';

export interface ITableMappingDropdownState {
  mappings: IDropdownOption[];
  mappingsLoadStatus: ELoadStatus;
}

@Injectable()
export class TableMappingDropdownStore extends ComponentStore<ITableMappingDropdownState> {
  private readonly http = inject(HttpClient);
  private readonly globalStore = inject<Store<TReducerState>>(Store);
  private readonly dashboardId = this.globalStore.selectSignal(
    ({ dashboardContent }) => dashboardContent.dashboardData?.id,
  );

  public readonly mappings = this.selectSignal(({ mappings }) => mappings);

  public readonly loadMappings = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => {
        this.patchState({
          mappingsLoadStatus: ELoadStatus.loading,
        });

        return this.getMappings().pipe(
          tapResponse(
            (response: IGetManyResponse<IDropdownOption>) => {
              this.patchState({
                mappings: response.data.map(({ id, name }) => ({ id, name })),
                mappingsLoadStatus: ELoadStatus.success,
              });
            },
            () => {
              this.patchState({
                mappingsLoadStatus: ELoadStatus.failure,
              });
            },
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      mappings: [],
      mappingsLoadStatus: ELoadStatus.initial,
    });
  }

  private getMappings(): Observable<IGetManyResponse<IDropdownOption>> {
    const dashboardId: number | undefined = this.dashboardId();

    return this.http.get<IGetManyResponse<IDropdownOption>>('table-view-widget-mappings', {
      headers: dashboardId ? { [WIDGET_DASHBOARD_ID_HEADER]: String(dashboardId) } : {},
    });
  }
}
